/**
 * Item Comparison System - Calculates and renders stat differences
 * between a hovered item and the currently equipped item in the same slot.
 * Used alongside the existing ItemTooltip for enhanced comparison display.
 */

import { EquipmentItem, ItemMod } from '@shared/types';
import { t } from '@locales/index';

export interface StatComparison {
  statName: string;
  displayName: string;
  currentValue: number;
  newValue: number;
  difference: number; // positive = better for the player
  isPercent: boolean;
}

function getStatDisplayName(stat: string): string {
  const key = `items.mods.${stat}`;
  const result = t(key);
  return result !== key ? result : stat;
}

// Stats where positive = percentage display
const PERCENT_STATS = new Set([
  'attackSpeed', 'critChance', 'critDamage', 'moveSpeed',
  'allResist', 'gokResist', 'odResist', 'suResist', 'karanlikResist',
  'physicalResist', 'lifeLeech', 'manaLeech', 'castSpeed', 'cooldownReduction',
  'blockChance', 'physicalDamage', 'elementalDamage', 'spellDamage',
]);

/**
 * Sum all mod values for a given stat type from an item's implicit, prefixes, and suffixes.
 */
function sumModStat(item: EquipmentItem | null, statType: string): number {
  if (!item) return 0;
  let total = 0;
  const allMods: ItemMod[] = [
    ...(item.implicit || []),
    ...(item.prefixes || []),
    ...(item.suffixes || []),
  ];
  for (const mod of allMods) {
    if (mod.type === statType) total += mod.value;
  }
  return total;
}

/**
 * Collect all unique stat types present in an item.
 */
function getItemStatTypes(item: EquipmentItem | null): Set<string> {
  const stats = new Set<string>();
  if (!item) return stats;
  const allMods: ItemMod[] = [
    ...(item.implicit || []),
    ...(item.prefixes || []),
    ...(item.suffixes || []),
  ];
  for (const mod of allMods) {
    stats.add(mod.type);
  }
  return stats;
}

/**
 * Compare two equipment items and return an array of stat differences.
 * Only stats that differ between the two items are included.
 */
export function compareItems(
  newItem: EquipmentItem,
  equippedItem: EquipmentItem | null,
): StatComparison[] {
  const comparisons: StatComparison[] = [];

  // Collect all unique stat types from both items
  const allStats = new Set<string>();
  const newStats = getItemStatTypes(newItem);
  const eqStats = getItemStatTypes(equippedItem);
  newStats.forEach(s => allStats.add(s));
  eqStats.forEach(s => allStats.add(s));

  for (const stat of allStats) {
    const newVal = sumModStat(newItem, stat);
    const curVal = sumModStat(equippedItem, stat);
    if (newVal !== curVal) {
      comparisons.push({
        statName: stat,
        displayName: getStatDisplayName(stat),
        currentValue: curVal,
        newValue: newVal,
        difference: newVal - curVal,
        isPercent: PERCENT_STATS.has(stat),
      });
    }
  }

  // Sort: improvements first, then losses
  comparisons.sort((a, b) => {
    if (a.difference > 0 && b.difference <= 0) return -1;
    if (a.difference <= 0 && b.difference > 0) return 1;
    return Math.abs(b.difference) - Math.abs(a.difference);
  });

  return comparisons;
}

/**
 * Render a compact comparison summary panel next to the item tooltip.
 * Returns the height of the rendered panel (0 if nothing was drawn).
 */
export function renderItemComparisonPanel(
  ctx: CanvasRenderingContext2D,
  comparisons: StatComparison[],
  x: number,
  y: number,
  width: number,
): number {
  if (comparisons.length === 0) return 0;

  const lineHeight = 18;
  const padding = 8;
  const headerHeight = lineHeight;
  const height = comparisons.length * lineHeight + padding * 2 + headerHeight;

  ctx.save();

  // Background
  ctx.fillStyle = 'rgba(15, 12, 25, 0.93)';
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, 4);
  ctx.fill();

  // Border
  ctx.strokeStyle = '#555566';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Header
  ctx.font = 'bold 11px Arial';
  ctx.fillStyle = '#BBBBCC';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(t('items.compare'), x + width / 2, y + padding);

  // Separator line under header
  const sepY = y + padding + headerHeight - 2;
  ctx.strokeStyle = 'rgba(100, 100, 140, 0.4)';
  ctx.beginPath();
  ctx.moveTo(x + padding, sepY);
  ctx.lineTo(x + width - padding, sepY);
  ctx.stroke();

  let drawY = y + padding + headerHeight;

  ctx.font = '10px Arial';
  ctx.textBaseline = 'top';

  for (const comp of comparisons) {
    const suffix = comp.isPercent ? '%' : '';

    // Stat name (left-aligned)
    ctx.textAlign = 'left';
    ctx.fillStyle = '#AAAAAA';
    ctx.fillText(comp.displayName, x + padding, drawY + 3);

    // Difference value (right-aligned)
    ctx.textAlign = 'right';
    if (comp.difference > 0) {
      ctx.fillStyle = '#44FF44'; // Green = improvement
      ctx.fillText(`+${comp.difference}${suffix}`, x + width - padding, drawY + 3);
    } else {
      ctx.fillStyle = '#FF4444'; // Red = downgrade
      ctx.fillText(`${comp.difference}${suffix}`, x + width - padding, drawY + 3);
    }

    drawY += lineHeight;
  }

  ctx.restore();
  return height;
}
